import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/category/dianshiju', label: t('nav.category') },
    { path: '/articles', label: t('nav.article') },
    { path: '/about', label: t('nav.about') },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <nav className="max-w-[1440px] mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        <Link
          to="/"
          className="flex items-center gap-2 no-underline"
          aria-label={t('site.name')}
        >
          <span className={`font-display text-2xl ${isScrolled ? 'text-primary-500' : 'text-background-50'}`}>
            🍑 {t('site.name')}
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`whitespace-nowrap text-sm font-medium transition-colors duration-200 no-underline ${
                location.pathname === link.path || (link.path !== '/' && location.pathname.startsWith(link.path))
                  ? isScrolled
                    ? 'text-primary-500'
                    : 'text-primary-300'
                  : isScrolled
                    ? 'text-foreground-700 hover:text-primary-500'
                    : 'text-background-50/80 hover:text-background-50'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <div className="relative">
            <input
              type="text"
              placeholder={t('nav.search')}
              className={`pl-9 pr-4 py-2 rounded-full text-sm border transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-400/50 ${
                isScrolled
                  ? 'bg-background-100 border-background-200 text-foreground-900 placeholder:text-foreground-400'
                  : 'bg-background-50/20 border-background-50/30 text-background-50 placeholder:text-background-50/50'
              }`}
            />
            <i className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm ${isScrolled ? 'text-foreground-400' : 'text-background-50/60'}`}></i>
          </div>
          <a
            href="#"
            className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2 rounded-full text-sm font-medium transition-all duration-200 no-underline"
          >
            {t('nav.appDownload')}
          </a>
        </div>

        <button
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg ${isScrolled ? 'text-foreground-900' : 'text-background-50'}`}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="菜单"
        >
          <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </nav>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-100 shadow-lg">
          <div className="px-4 py-4 flex flex-col gap-3">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`whitespace-nowrap text-sm font-medium px-3 py-2 rounded-lg transition-colors no-underline ${
                  location.pathname === link.path
                    ? 'bg-primary-50 text-primary-600'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="relative mt-2">
              <input
                type="text"
                placeholder={t('nav.search')}
                className="w-full pl-9 pr-4 py-2.5 rounded-full text-sm bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400/50"
              />
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
            </div>
            <a
              href="#"
              className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2.5 rounded-full text-sm font-medium text-center transition-all duration-200 no-underline"
            >
              {t('nav.appDownload')}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}